import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, Renderer2, inject, DOCUMENT } from '@angular/core';

type StadiaResource =
  | { type: 'style' | 'script' | 'json'; href: string }
  | { type: 'module'; name: string };

@Injectable({ providedIn: 'root' })
export class StadiaMapsLoaderService {
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private prefetchCalled = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private loadingPromise: Promise<void[]>;

  private resources: StadiaResource[] = [
    { type: 'style', href: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css' },
    { type: 'module', name: 'leaflet' },
  ];

  prefetchResources() {
    if (this.prefetchCalled || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.resources.forEach(resource => {
      if (resource.type === 'module') {
        this.importModule(resource.name);
        return;
      }

      const link: HTMLLinkElement = this.document.createElement('link');

      link.rel = 'prefetch';
      link.as = resource.type === 'json' ? 'fetch' : resource.type;
      link.href = resource.href;

      this.document.head.appendChild(link);
    });
    this.prefetchCalled = true;
  }

  loadResources(renderer: Renderer2) {
    if (!this.loadingPromise) {
      const promises = this.resources.map(resource => {
        if (resource.type === 'module') {
          return this.importModule(resource.name).then(() => {});
        }

        return new Promise<void>((resolve, reject) => {
          let element: HTMLLinkElement | HTMLScriptElement;

          if (resource.type === 'script') {
            element = <HTMLScriptElement>this.document.createElement('script');

            element.src = resource.href;
            element.async = false;
            element.defer = true;
          } else {
            element = <HTMLLinkElement>this.document.createElement('link');

            element.href = resource.href;
            element.rel = 'stylesheet';
          }

          element.onload = () => {
            resolve();
          };
          element.onerror = () => {
            console.log('onerror');

            reject();
          };

          renderer.appendChild(this.document.head, element);
        });
      });

      this.loadingPromise = Promise.all(promises);
    }

    return this.loadingPromise;
  }

  async getLeaflet(): Promise<typeof import('leaflet')> {
    const mod = await this.importModule('leaflet');

    return (mod as { default?: typeof import('leaflet') }).default || mod;
  }

  private importModule(name: string) {
    switch (name) {
      case 'leaflet':
        return import('leaflet');
      default:
        return Promise.reject(new Error(`Unknown module: ${name}`));
    }
  }
}
